import { useEffect, useState } from "react";
import "../../../assets/css/hrmSettings.css";
import { useToast } from "../../../context/ToastContext";
import BaseModal from "../../../components/BaseModal";

const API_BASE = `${process.env.REACT_APP_API_BASE}/hrm/hrm_settings.php`;

const defaultSettings = {
  departments: [],
roles: [],
designations: [],
  grades: [],
  branches: [],
  employmentTypes: [],
};

export default function HRMSettings() {
  const { showToast } = useToast();

  const [settings, setSettings] = useState(defaultSettings);
  const [inputs, setInputs] = useState({
    departments: "",
   roles: "",
designations: "",
    grades: "",
    branches: "",
    employmentTypes: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const res = await fetch(API_BASE, {
        method: "GET",
        credentials: "include",
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to load HRM settings");
      }

      setSettings({
        departments: result.data?.departments || [],
        roles: result.data?.roles || [],
         designations: result.data?.designations || [],
        grades: result.data?.grades || [],
        branches: result.data?.branches || [],
        employmentTypes: result.data?.employmentTypes || [],
      });
    } catch (error) {
      showToast("error", "Load Failed", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const addItem = (key) => {
    const value = inputs[key].trim();

    if (!value) {
      showToast("error", "Input Required", "Please enter a value.");
      return;
    }

    if (settings[key].some((item) => item.toLowerCase() === value.toLowerCase())) {
      showToast("error", "Duplicate Entry", "This item already exists.");
      return;
    }

    setSettings((prev) => ({
      ...prev,
      [key]: [...prev[key], value],
    }));

    setInputs((prev) => ({
      ...prev,
      [key]: "",
    }));

    showToast("success", "Added", "Item added successfully.");
  };

  const removeItem = (key, index) => {
    setSettings((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));

    showToast("info", "Removed", "Click Save Settings to apply changes.");
  };

  const saveSettings = async () => {
const hasData =
  settings.departments.length ||
  settings.roles.length ||
  settings.designations.length ||
  settings.grades.length ||
    settings.branches.length ||
    settings.employmentTypes.length;

  if (!hasData) {
    showToast("error", "Validation Error", "Please add at least one item before saving.");
    return;
  }

  try {
    setSaving(true);

      const res = await fetch(API_BASE, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(settings),
      });

      const result = await res.json();

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to save HRM settings");
      }

     setConfirmSaveOpen(false);
showToast("success", "Saved", "HRM settings saved successfully.");
await loadSettings();
    } catch (error) {
      showToast("error", "Save Failed", error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="hrm-settings-page">
        <div className="hrm-settings-card">
          <h4>Loading HRM settings...</h4>
          <p>Please wait while we fetch your configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hrm-settings-page">
      <div className="hrm-settings-header">
        <div>
          <h2>HRM Settings</h2>
          <p>Manage departments, roles, grades, branches and employment types.</p>
        </div>

      
      </div>

      <div className="hrm-settings-grid">
        <SettingCard
          title="Departments"
          description="Add departments used when creating staff records."
          settingKey="departments"
          placeholder="e.g. Human Resources"
          settings={settings}
          inputs={inputs}
          setInputs={setInputs}
          addItem={addItem}
          removeItem={removeItem}
        />

        <SettingCard
          title="Roles"
          description="Add system or staff roles."
          settingKey="roles"
          placeholder="e.g. HR Admin"
          settings={settings}
          inputs={inputs}
          setInputs={setInputs}
          addItem={addItem}
          removeItem={removeItem}
        />

        <SettingCard
  title="Designations"
  description="Add staff designations used for approval routing."
  settingKey="designations"
  placeholder="e.g. Credit Officer"
  settings={settings}
  inputs={inputs}
  setInputs={setInputs}
  addItem={addItem}
  removeItem={removeItem}
/>

        <SettingCard
          title="Grades / Levels"
          description="Add staff grades or levels."
          settingKey="grades"
          placeholder="e.g. Level 1"
          settings={settings}
          inputs={inputs}
          setInputs={setInputs}
          addItem={addItem}
          removeItem={removeItem}
        />

        <SettingCard
          title="Branches"
          description="Add company branches or office locations."
          settingKey="branches"
          placeholder="e.g. Head Office"
          settings={settings}
          inputs={inputs}
          setInputs={setInputs}
          addItem={addItem}
          removeItem={removeItem}
        />

        <SettingCard
          title="Employment Types"
          description="Add employment categories."
          settingKey="employmentTypes"
          placeholder="e.g. Full Time"
          settings={settings}
          inputs={inputs}
          setInputs={setInputs}
          addItem={addItem}
          removeItem={removeItem}
        />
      </div>

      <div className="hrm-settings-footer">
      <button
  className="hrm-save-btn"
  onClick={() => setConfirmSaveOpen(true)}
disabled={
  saving ||
  (!settings.departments.length &&
  !settings.roles.length &&
!settings.designations.length &&
!settings.grades.length &&
    !settings.branches.length &&
    !settings.employmentTypes.length)
}
>
  <i className="fa fa-save" /> Save Settings
</button>
      </div>

      

<BaseModal
  isOpen={confirmSaveOpen}
  title="Confirm Save"
  onClose={() => setConfirmSaveOpen(false)}
  size="sm"
  showClose={true}
  icon={<i className="fa fa-shield-alt" />}
  footer={
    <>
      <button
        type="button"
        className="btn-outline"
        onClick={() => setConfirmSaveOpen(false)}
        disabled={saving}
      >
        Cancel
      </button>

      <button
        type="button"
        className="hrm-save-btn"
        onClick={saveSettings}
        disabled={saving}
      >
        {saving ? "Saving..." : <><i className="fa fa-check" /> Confirm Save</>}
      </button>
    </>
  }
>
  <p>Are you sure you want to save these HRM settings?</p>
</BaseModal>
    </div>
  );
}

function SettingCard({
  title,
  description,
  settingKey,
  placeholder,
  settings,
  inputs,
  setInputs,
  addItem,
  removeItem,
}) {
  return (
    <div className="hrm-settings-card">
      <h4>{title}</h4>
      <p>{description}</p>

      <div className="hrm-add-row">
        <input
          value={inputs[settingKey]}
          placeholder={placeholder}
          onChange={(e) =>
            setInputs((prev) => ({
              ...prev,
              [settingKey]: e.target.value,
            }))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              addItem(settingKey);
            }
          }}
        />

      <button type="button" onClick={() => addItem(settingKey)}>
  <i className="fa fa-plus" /> Add
</button>
      </div>

      <div className="hrm-list">
        {settings[settingKey].length === 0 ? (
          <div className="hrm-empty">No item added yet.</div>
        ) : (
          settings[settingKey].map((item, index) => (
            <div className="hrm-item" key={`${settingKey}-${index}`}>
              <span>{item}</span>
              <button type="button" onClick={() => removeItem(settingKey, index)}>
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}